import type { ColumnFilterState } from '../types/table';

// Helper function to build query params for contract-based endpoints
const buildContractQueryParams = (
  payer?: string,
  state?: string,
  lineOfBusiness?: string,
  start_after_id?: string,
  start_after_sort_value?: string,
  search?: string,
  sort_by?: string,
  sort_direction?: string,
  column_filters?: Record<string, ColumnFilterState>
): string => {
  const params = new URLSearchParams();
  if (payer) {
    params.append('payer', payer);
  }
  if (state) {
    params.append('state', state);
  }
  if (lineOfBusiness) {
    params.append('line_of_business', lineOfBusiness);
  }
  if (start_after_id) {
    params.append('start_after_id', start_after_id);
  }
  // start_after_sort_value can legitimately be "" (e.g. a contract with no effective_date,
  // sorted into ES's missing_bucket) -- send it whenever a cursor is being paginated, not
  // just when it's truthy, or the after-key round trip loses that it was explicitly empty.
  if (start_after_sort_value !== undefined) {
    params.append('start_after_sort_value', start_after_sort_value);
  }
  if (search) {
    // URLSearchParams already percent-encodes values; encodeURIComponent here would
    // double-encode (e.g. "Contract A.pdf" -> "Contract%2520A.pdf").
    params.append('search', search);
  }
  if (sort_by) {
    params.append('sort_by', sort_by);
  }
  if (sort_by && sort_direction) {
    params.append('sort_direction', sort_direction);
  }
  if (column_filters && Object.keys(column_filters).length > 0) {
    params.append('column_filters', JSON.stringify(column_filters));
  }
  return params.toString();
};

const baseUrlFromEnv = (typeof import.meta !== 'undefined' && import.meta.env?.REACT_APP_API_BASE_URL) || '';

const formatBaseUrl = (version: string) => {
  return baseUrlFromEnv
    ? `${baseUrlFromEnv.replace(/\/+$/, "")}/${version}`
    : `/api/${version}`;
};

const API_VERSION = 'v1';
const API_VERSION_V2 = 'v2';
const BASE_URL = formatBaseUrl(API_VERSION)
const BASE_URL_V2 = formatBaseUrl(API_VERSION_V2)

export const API_CONFIG = {
  BASE_URL,
  BASE_URL_V2,
  VERSION: API_VERSION,
  
  // Endpoints
  ENDPOINTS: {
    // Chat Management
    CHATS: '/chats/',
    CHATS_REORDER: '/chats/reorder/',
    CHAT_MESSAGES: (chatId: string) => `/chats/${chatId}/messages/`,
    CHAT_MESSAGE_FEEDBACK: (chatId: string, messageId: string) => `/chats/${chatId}/messages/${messageId}/feedback/`,
    /** One letter version, or the latest; `?format=` picks json, docx or pdf. */
    CHAT_OUTPUT_DOCUMENT: (chatId: string, version: number | "latest") =>
      `/chats/${chatId}/output-documents/${version}/`,

    // File Management
    FILE_DOWNLOAD: (fileId: string) => `/files/${fileId}/download/`,
    DOCUMENT_BY_ID_DOWNLOAD: (documentId: string) => `/tenants/documents/${documentId}/download/`,
    
    // Payers
    PAYERS: '/payers/',
        // Browsable policy research table (payer optional) -- mirrors CONTRACTS_BY_TENANT's cursor/sort/column-filter shape.
    POLICIES_BY_TENANT: (payer?: string, state?: string, lineOfBusiness?: string, start_after_id?: string, start_after_sort_value?: string, search?: string, sort_by?: string, sort_direction?: string, column_filters?: Record<string, ColumnFilterState>) =>
      `/policies/?${buildContractQueryParams(payer, state, lineOfBusiness, start_after_id, start_after_sort_value, search, sort_by, sort_direction, column_filters)}`,
    CONTRACT_PAYERS: () => `/tenant/payers/`,
    CONTRACTS_BY_TENANT: (payer?: string, state?: string, lineOfBusiness?: string, start_after_id?: string, start_after_sort_value?: string, search?: string, sort_by?: string, sort_direction?: string, column_filters?: Record<string, ColumnFilterState>) =>
      `/tenants/contracts/?${buildContractQueryParams(payer, state, lineOfBusiness, start_after_id, start_after_sort_value, search, sort_by, sort_direction, column_filters)}`,
    
    // Letter Format
    LETTER_FORMAT: '/letter-format/',

    // Api Key
    API_KEY: '/auth/api-keys/',
    API_KEY_ROTATE: (id: string) => `/api-keys/${id}/rotate`,
    API_KEY_ALLOWLIST: (id: string) => `/api-keys/${id}/allowlist/`,
    
    // Claims
    CLAIMS_UPLOAD: '/claims/upload/',
    CLAIMS_STATISTICS: '/claims/statistics/',
    CLAIMS_DASHBOARD_STATISTICS: '/claims/dashboard-statistics/',
    CLAIMS_DATA: '/claims/data/',
    CLAIMS_DATA_DOWNLOAD: '/claims/data/download/',
    CLAIMS_DATA_BY_USER: '/claims/user-data/',
    CLAIMS_SUMMARY: '/claims/summary/',
    CLAIM_DETAIL: (claimId: string) => `/claims/${claimId}/`,
    CLAIMS_FILTER_OPTIONS: '/claims/filter-options/',
    CLAIM_UPDATE_ASSIGNED_USER: (claimId: string) => `/claims/${claimId}/assigned-user/`,
    CLAIM_APPEALS: (claimId: string) => `/claims/${claimId}/appeals/`,
    CLAIM_APPEALS_PREVIEW: (claimId: string) => `/claims/${claimId}/appeals/preview/`,
    CLAIM_DOCUMENT: (claimId: string, documentId: string) => `/claims/${claimId}/documents/${documentId}/`,
    CLAIM_MANUAL_PAYMENTS: (claimId: string) => `/claims/${claimId}/payments/manual/`,
    CLAIM_MANUAL_PAYMENTS_PREVIEW: (claimId: string) => `/claims/${claimId}/payments/manual/preview/`,
    CLAIM_MANUAL_PAYMENT_REMOVE: (claimId: string, attachmentId: string) => `/claims/${claimId}/payments/manual/${attachmentId}/remove/`,
    CLAIM_MANUAL_PAYMENT_JOIN: (claimId: string, attachmentId: string) => `/claims/${claimId}/payments/manual/${attachmentId}/join/`,
    CLAIM_LINK_CANDIDATES: (claimId: string) => `/claims/${claimId}/link-candidates/`,
    CLAIM_LINKS: (claimId: string) => `/claims/${claimId}/links/`,
    CLAIM_LINKS_PREVIEW: (claimId: string) => `/claims/${claimId}/links/preview/`,
    CLAIM_LINK_UNLINK: (claimId: string, linkId: string) => `/claims/${claimId}/links/${linkId}/unlink/`,
    
    // Claims Filter Views
    CREATE_CLAIM_FILTERS_VIEW: '/claims/filter-views/',
    CLAIMS_FILTER_VIEWS: '/claims/filter-views/',
    CLAIM_FILTER_VIEW: (viewId: string) =>
      `/claims/filter-views/${encodeURIComponent(viewId)}/`,
    
    // Upload History Dashboard
    UPLOAD_HISTORY_DAILY: '/claims/upload-history/daily/',
    UPLOAD_HISTORY_FILES: '/claims/upload-history/files/',
    UPLOAD_HISTORY_FILES_DOWNLOAD: '/claims/upload-history/files/download/',
    UPLOAD_HISTORY_REPROCESS: '/claims/upload-history/reprocess/',
    UPLOAD_HISTORY_FILE_DETAIL: (fileId: string) => `/claims/upload-history/files/${fileId}/`,

    // Claim Analysis Files Upload (Customer support)
    CLAIM_ANALYSIS_FILES_UPLOAD: '/users/upload/',
    CLAIM_ANALYSIS_FILE_CHUNKED_UPLOAD: '/users/upload-chunked/',

    // Observability
    USAGE_BY_AGENT: '/usage/by-agent-type',
    USAGE_CASES_BREAKDOWN: '/usage/cases-breakdown',
    USAGE_KPIS: '/usage/kpis',
    CLAIM_OUTCOME_TRACKING: '/usage/claim-outcomes',
    USAGE_ACTIVITY_FEED: '/usage/activity-feed',
    USAGE_ACTIVITY_FEED_DOWNLOAD: '/usage/activity-feed/download/',
    USAGE_ACTIVITY_FEED_OPTIONS: '/usage/activity-feed-options',
    FINANCIAL_IMPACT: '/usage/billing/financial-impact',
    SUCCESS_FEES_PER_CLAIM: '/usage/billing/success-fees-per-claim',
    SUCCESS_FEES_PER_CLAIM_DOWNLOAD: '/usage/billing/success-fees-per-claim/download/',
    SUCCESS_FEES_PER_CLAIM_OPTIONS: '/usage/billing/success-fees-per-claim-options',
    DRILL_DOWN_CAPABILITY_LINE_ITEMS: '/usage/billing/compute/line-items',
    DRILL_DOWN_CAPABILITY_COMPUTE: '/usage/billing/compute',

    // Release Notes
    RELEASE_VERSIONS: '/release-notes/data/',

    // Tickets
    TICKETS: '/tickets/',
    TICKET_DETAIL: (ticketId: number) => `/tickets/${ticketId}/`,
    TICKET_ARTICLES: (ticketId: number) => `/tickets/${ticketId}/articles/`,
    TICKET_REOPEN: (ticketId: number) => `/tickets/${ticketId}/articles/reopen-ticket/`,
    TICKET_ATTACHMENT: (ticketId: number, articleId: number, attachmentId: number) =>
      `/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}/`,
  },
  
  // HTTP Status Codes
  STATUS_CODES: {
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    CONFLICT: 409,
    UNPROCESSABLE_ENTITY: 422,
    INTERNAL_SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
  },
  
  TIMEOUTS: {
    DEFAULT: 30000,     // 30 seconds
    UPLOAD: 120000,     // 2 minutes
    PROCESSING: 300000, // 5 minutes
  },
  
  UPLOAD_LIMITS: {
    MAX_FILE_SIZE: 9500000, // ~9.5 MB (< 10 MB)
    MAX_FILES: 10,
    ALLOWED_TYPES: [
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/tiff',
      'text/csv',
      'application/json',
      'application/xml',
    ],
  },
  
} as const;

